import { useEffect, useReducer, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { App, AppStatus, AuthenticationError, AuthorizationError, CustomDomainStatus, DeploymentMemoryExceededError, DeploymentLog, DockerfileNotFoundError, NetworkError, Subscription, User, DeploymentStatus, KanikoImageBuildError, ContainerLog, ContainerStats, GitHubResourceNotFoundError, GitRepositoryEmptyError, CreditCardValidationRequiredError } from '@back4app2/sdk';
import GitHubReconnectModal from '../components/GitHubReconnectModal';
import MyAppDeploymentStatus from '../components/MyAppDeploymentStatus';
import Timer from '../components/Timer';
import CancelBuildButton, { isBuildCancelable } from '../components/CancelBuildButton';
import { timeFromNow } from '../utils/dates';
import back4app2 from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import LogBox from '../components/LogBox';
import { hasFinishedBuilding } from '../utils/deploymentBuild';
import Guides from '../components/Guides';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import DockerfileErrorMessage from '../components/CustomErrorMessageUI/DockerfileErrorMessage';
import VerifyEmailErrorMessage from '../components/CustomErrorMessageUI/VerifyEmailErrorMessage';
import SecurityBlockErrorMessage from '../components/CustomErrorMessageUI/SecurityBlockErrorMessage';
import PendingSetupMessage from '../components/CustomErrorMessageUI/PendingSetupMessage';
import OutOfMemoryError from '../components/CustomErrorMessageUI/OutOfMemoryError';
import PaymentPendingErrorMessage from '../components/CustomErrorMessageUI/PaymentPendingErrorMessage';
import ImageBuildError from '../components/CustomErrorMessageUI/ImageBuildError';
import { getContainerStatsGraphData } from '../utils/metrics';
import ContainerLogBox from '../components/ContainerLogBox';
import TemporaryUrlNotice from '../components/TemporaryUrlNotice';
import PaymentPastDueNotice from '../components/PaymentPastDueNotice';
import type { SettingsFocus } from '../utils/settingsFocus';
import { Eyebrow, MetricChart, ScreenHead, SegmentedControl, StatCard, StatusDot, SurfaceCard, TileRow } from '../components/ds';
import { BoxIcon, BranchIcon, ChevronRightIcon, ExternalLinkIcon, FileIcon, FolderIcon, GlobeIcon, HeartbeatIcon, LinkIcon, PortIcon, TerminalIcon, UploadIcon, VariablesIcon } from '../components/ds/icons';
import { getAppStatusSummary } from '../utils/appStatus';
import { appSourceKind, isBuiltFromSource } from '../utils/appSource';
import InstantRollbackButton from '../components/Rollback/InstantRollbackButton';
import { MyAppSettingsTab } from './MyAppSettings';
import useUpgrade from '../hooks/useUpgrade';
import ResourceNotAccessibleError from '../components/CustomErrorMessageUI/ResourceNotAccessibleError';
import CreditCardValidationRequiredErrorMessage from '../components/CustomErrorMessageUI/CreditCardValidationRequiredErrorMessage';

interface MyAppOverviewState {
  isLoadingDeploymentLogs: boolean;
  deploymentLogsLoadingErrorMessage?: string;
  deploymentLogs?: DeploymentLog[];
  isLoadingStats: boolean;
  loadingStatsErrorMessage?: string;
  colors?: { [containerId: string]: string };
  cpuGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  ramGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max: number};
  isLoadingContainerLogs: boolean;
  containerLogsLoadingErrorMessage?: string;
  containerLogs?: ContainerLog[];
}

const INITIAL_STATE: MyAppOverviewState = {
  isLoadingDeploymentLogs: true,
  isLoadingStats: true,
  isLoadingContainerLogs: true
};

enum MyAppOverviewActionType {
  RESET,
  FINISH_LOADING_DEPLOYMENT_LOGS,
  FINISH_LOADING_STATS,
  FINISH_LOADING_CONTAINER_LOGS,
  RESET_DEPLOYMENT_LOGS,
  RESET_CONTAINER_STATS,
  RESET_CONTAINER_LOGS,
}

const reset = () => ({
  type: MyAppOverviewActionType.RESET
} as const);

const resetDeploymentLogs = () => ({
  type: MyAppOverviewActionType.RESET_DEPLOYMENT_LOGS
} as const);
const resetContainerLogs = () => ({
  type: MyAppOverviewActionType.RESET_CONTAINER_LOGS
} as const);
const resetContainerStats = () => ({
  type: MyAppOverviewActionType.RESET_CONTAINER_STATS
} as const);

const finishLoadingDeploymentLogs = (errorMessage?: string, deploymentLogs?: DeploymentLog[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_DEPLOYMENT_LOGS,
  payload: {
    errorMessage,
    deploymentLogs
  }
} as const);

const finishLoadingStats = (errorMessage?: string, stats?: ContainerStats[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_STATS,
  payload: {
    errorMessage,
    stats
  }
} as const);

const finishLoadingContainerLogs = (errorMessage?: string, logs?: ContainerLog[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_CONTAINER_LOGS,
  payload: {
    errorMessage,
    logs
  }
} as const);

const containerStatsTimeWindow = 1 * 60 * 60 * 1000;

type MyAppOverviewAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingDeploymentLogs> | ReturnType<typeof finishLoadingContainerLogs>  | ReturnType<typeof finishLoadingStats>  | ReturnType<typeof resetDeploymentLogs>  | ReturnType<typeof resetContainerLogs>  | ReturnType<typeof resetContainerStats>;

const reducer = (state: MyAppOverviewState = INITIAL_STATE, action: MyAppOverviewAction): MyAppOverviewState => {
  switch (action.type) {
    case MyAppOverviewActionType.RESET: 
      return INITIAL_STATE;

    case MyAppOverviewActionType.RESET_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: true,
        deploymentLogsLoadingErrorMessage: undefined,
        deploymentLogs: undefined

      }
    case MyAppOverviewActionType.RESET_CONTAINER_LOGS:
      return {
        ...state,
        isLoadingContainerLogs: true,
        containerLogsLoadingErrorMessage: undefined,
        containerLogs: undefined
      }

    case MyAppOverviewActionType.RESET_CONTAINER_STATS:
      return {
        ...state,
        isLoadingStats: true,
        loadingStatsErrorMessage: undefined,
        colors: undefined,
        cpuGraphProps: undefined,
        ramGraphProps: undefined
      }

    case MyAppOverviewActionType.FINISH_LOADING_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: false,
        deploymentLogsLoadingErrorMessage: action.payload.errorMessage,
        deploymentLogs: action.payload.deploymentLogs
      }
    case MyAppOverviewActionType.FINISH_LOADING_STATS: {
      const { colors,
        cpuGraphProps,
        ramGraphProps,
      } = getContainerStatsGraphData(action.payload.stats || [], state.colors)
      
      return {
        ...state,
        isLoadingStats: false,
        loadingStatsErrorMessage: action.payload.errorMessage,
        colors,
        cpuGraphProps,
        ramGraphProps
      };
    }
    case MyAppOverviewActionType.FINISH_LOADING_CONTAINER_LOGS:
      return {
        ...state,
        isLoadingContainerLogs: false,
        containerLogsLoadingErrorMessage: action.payload.errorMessage,
        containerLogs: action.payload.logs
      };
  }
};

// The CPU and RAM cards on the Overview are a glance, not the Metrics screen:
// flat enough that the plan card beside them is not stretched into a tall row
// with most of it empty. The three Y ticks still clear each other at this size.
const OVERVIEW_PLOT_HEIGHT = 64;

const MyAppOverview = () => {
  const { app, appLoadedAt, user, goToSettingsTab } = useOutletContext<{
    app: App,
    /** When the shell fetched the app — the Timer counts up from it. */
    appLoadedAt: Date,
    user: User,
    /** The shell owns it: it asks before dropping unsaved edits. */
    goToSettingsTab: (id: string, focus?: SettingsFocus) => void,
  }>();
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;
  const lastDeployment = app && app.mainService && app.mainService.mainServiceEnvironment && app.mainService.mainServiceEnvironment.lastDeployment;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // WHICH failure was waved away, not merely that one was. A page-session flag
  // silenced every later failure too: dismiss it once — and any click outside
  // the modal counts as dismissing — and the next failed deploy offered
  // nothing, on a screen that had just updated itself to show that failure.
  // Reloading was the only way back to the offer.
  const [githubReconnectDismissedFor, setGithubReconnectDismissedFor] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const upgrade = useUpgrade(app.id);

  // The code, not the log text. The worker writes GITHUB_INSTALLATION_INVALID
  // on the deployment precisely so a screen can branch on it — matching the
  // sentence in the log panel would break the day someone rewords it.
  const needsGithubReconnect =
    lastDeployment?.status === DeploymentStatus.FAILED &&
    lastDeployment?.errorCode === 'GITHUB_INSTALLATION_INVALID';
  const { deploymentLogsLoadingErrorMessage, deploymentLogs, isLoadingDeploymentLogs, containerLogsLoadingErrorMessage, containerLogs, isLoadingContainerLogs, isLoadingStats, loadingStatsErrorMessage, cpuGraphProps, ramGraphProps } = state;

  // Build log and runtime log used to be two nearly identical panels stacked
  // on the same page. One panel, two tabs — nothing is unreachable, and the
  // log gets the width it always needed.
  const [logTab, setLogTab] = useState<'build' | 'runtime'>('build');
  /**
   * How many domains answer for this app.
   *
   * `mainCustomDomain` comes with the app, but it is one of possibly several
   * and the summary counts them. A number is all this screen wants, so it
   * stays in local state rather than going through the reducer — and a failure
   * leaves it `undefined`, which the row reads as "the main one, and no count".
   */
  const [domainCount, setDomainCount] = useState<number | undefined>(undefined);

  const status = getAppStatusSummary(app);

  const plan = mainServiceEnvironment?.plan;
  // A cancelled plan is not a former plan: it runs, and it was paid for, until
  // this date. The card would otherwise show the paid tier with no hint that it
  // has an end — and this is the screen people actually look at.
  const planCancelsAt = mainServiceEnvironment?.planCancelsAt
    ? new Date(mainServiceEnvironment.planCancelsAt)
    : undefined;
  const planEndsOn = planCancelsAt && planCancelsAt.getTime() > Date.now()
    ? planCancelsAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : undefined;
  const envVarCount = mainServiceEnvironment?.envVars?.length ?? 0;
  /**
   * The address the app is served on, or nothing.
   *
   * The same three conditions the sidebar's link is written under: there has
   * to be an environment, its main domain has to still answer — an EXPIRED one
   * has had its balancer rules pulled — and a blocked app must not advertise a
   * live address anywhere.
   */
  const appUrl = mainServiceEnvironment?.mainCustomDomain
    && mainServiceEnvironment.mainCustomDomain.status !== CustomDomainStatus.EXPIRED
    && !app.block
    ? mainServiceEnvironment.mainCustomDomain.name
    : undefined;
  const planRAM = plan?.maxRAM
    ? (plan.maxRAM >= 1024 ? `${plan.maxRAM / 1024}GB` : `${plan.maxRAM}MB`)
    : undefined;
  // The RAM chart plots raw bytes, and `maxRAM` is written in MB — the two
  // have to meet somewhere for the axis and the label to name one number.
  const ramLimitBytes = plan?.maxRAM ? plan.maxRAM * 1024 * 1024 : undefined;


  useEffect(
    () => {
      let subscription: Subscription;

      if (lastDeployment?.id) {
        subscription = back4app2.subscribeToDeploymentLogs(
          lastDeployment.id,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingDeploymentLogs('Network error when loading last deployment logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading last deployment logs', error);
                dispatch(finishLoadingDeploymentLogs('Unexpected error when loading last deployment logs. Please try again.'));
              }
            } else {
              dispatch(finishLoadingDeploymentLogs(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(resetDeploymentLogs());
        }
      };
    },
    [
      lastDeployment?.id
    ]
  );

  useEffect(() => {
    let statsSubscription: Subscription;
    if (mainServiceEnvironmentId) {
      statsSubscription = back4app2.subscribeToContainersStats(
        mainServiceEnvironmentId,
        containerStatsTimeWindow,
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error('network error', error);
              dispatch(finishLoadingStats('Network error when loading app stats. Check your internet connection and try again.'));
            } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
              window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
            } else {
              console.error('unexpected error loading app stats', error);
              dispatch(finishLoadingStats('Unexpected error when loading app stats. Please try again.'));
            }
          } else {
            dispatch(finishLoadingStats(undefined, snapshot));
          }
        }
      )
    }
  
    return () => {
      if (statsSubscription) {
        statsSubscription.unsubscribe();          
        dispatch(resetContainerStats());
      }
    }
  }, [mainServiceEnvironmentId]);

  useEffect(
    () => {
      let subscription: Subscription;

      if (mainServiceEnvironmentId) {
        subscription = back4app2.subscribeToContainersLogs(
          mainServiceEnvironmentId,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingContainerLogs('Network error when loading app logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app logs', error);
                dispatch(finishLoadingContainerLogs('Unexpected error when loading app logs. Please try again.'));
              }
            } else {
              dispatch(finishLoadingContainerLogs(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(resetContainerLogs());
        }
      };
    },
    [
      mainServiceEnvironmentId
    ]
  );

  useEffect(() => {
    let subscription: Subscription;

    if (mainServiceEnvironmentId) {
      subscription = back4app2.subscribeToCustomDomains(mainServiceEnvironmentId, (error, snapshot) => {
        if (error) {
          // A count nobody asked for is not worth a banner, and the row reads
          // without it. Sign-in is the exception, as everywhere else here.
          if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
            window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
          } else {
            console.error('unexpected error loading custom domains', error);
          }
        } else {
          setDomainCount(snapshot?.length);
        }
      });
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      setDomainCount(undefined);
    };
  }, [mainServiceEnvironmentId]);

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_OVERVIEW_PAGE);
    return () => {
      dispatch(reset())
    }
  }, []);

  const planCard = (
    <StatCard
      label="plan"
      value={plan?.name ?? '—'}
      unit={plan?.price ? `$${plan.price}.00 / mo` : undefined}
      tone={plan ? 'value' : 'empty'}
      note={plan
        ? (
          <>
            {[
              `${plan.maxCPU} vCPU`,
              planRAM,
              typeof plan.transferLimit === 'number' ? `${plan.transferLimit} GB transfer` : undefined,
            ].filter(Boolean).join(' · ')}
            {planEndsOn ? (
              <span className="ml-2 font-courrier text-ds-105 uppercase tracking-[0.08em] text-[#F5B544]">
                ends {planEndsOn}
              </span>
            ) : null}
          </>
        )
        : 'no plan on this app yet'}
      action={
        <button
          type="button"
          onClick={upgrade}
          className="b4-focus flex-none rounded-control text-ds-125 font-semibold text-action-soft hover:text-white"
        >
          {plan?.name.includes('Free') ? 'Upgrade' : 'Manage'}
        </button>
      }
    />
  );

  // Blocked by the security review: the card that says so, and the plan
  // under it, and nothing else. The readings, the log and the configuration
  // are of an app that is not running, and a board of zeros around the one
  // card that matters made it one panel among many.
  if (app.block) {
    return (<>
      <div className="hidden lg:flex items-start justify-between gap-3">
        <ScreenHead title="Overview">
          <span className="flex items-center gap-2 min-w-0">
            <StatusDot tone="error" halo />
            <span className="text-ds-125 text-navy-100 truncate">{status.text}</span>
          </span>
        </ScreenHead>
      </div>
      <SecurityBlockErrorMessage
        appId={app.id}
        block={app.block}
        userEmail={user.username}
      />
      <div className="lg:max-w-md">{planCard}</div>
    </>);
  }

  return (<>
    {/* Opens itself, once per failure, on the one it can fix. Dismissing
        records THAT deployment's id, so the modal does not come back on every
        re-render of a page that stays open — the failed deployment is still
        the last one, and without this the customer could not read the log
        underneath it. A later deploy that fails the same way is a different
        deployment, so it is offered again, on the open page rather than only
        after a reload. */}
    <GitHubReconnectModal
      open={needsGithubReconnect && githubReconnectDismissedFor !== lastDeployment?.id}
      onClose={() => setGithubReconnectDismissedFor(lastDeployment?.id)}
      serviceEnvironmentId={mainServiceEnvironmentId}
      repositoryId={app?.mainService?.repository?.id}
      repositoryFullName={app?.mainService?.repository?.fullName}
    />

    {/* The state rides beside the name as a dot and a line of small type
        rather than as one of the cards. It is the shortest fact on the page —
        one word and a timestamp — and a card the size of the CPU reading was
        the loudest thing here saying the quietest thing. The colour is the
        dot's job; the words stay the colour of every other note on the
        screen. */}
    {/* Desktop only: on a phone the app header carries the same dot, word
        and age, and the rollback is on the Deployments list. */}
    <div className="hidden lg:flex items-start justify-between gap-3">
      <ScreenHead title="Overview">
        <span className="flex items-center gap-2 min-w-0">
          <StatusDot tone={status.tone === 'ok' ? 'ok' : status.tone === 'error' ? 'error' : status.tone === 'warn' ? 'warn' : 'idle'} halo />
          <span className="text-ds-125 text-navy-100 truncate">{status.text}</span>
          {lastDeployment?.createdAt ? (
            <span className="font-courrier text-ds-115 text-navy-300 truncate">
              · {timeFromNow(lastDeployment.createdAt)}
            </span>
          ) : null}
        </span>
      </ScreenHead>
      {app ? <InstantRollbackButton app={app} /> : null}
    </div>

    {/* The phone's copy of the sidebar's notice. The sidebar that carries it
        is `hidden lg:flex`, and the header strip below it has room for the
        address and nothing after it — so on a phone this screen is the only
        place a free-plan customer can be told their URL is rented. */}
    <div className="lg:hidden flex flex-col gap-3">
      <TemporaryUrlNotice app={app} />
      <PaymentPastDueNotice app={app} onUpdated={() => window.location.reload()} />
    </div>

    {/* The plan, then the two readings it is the ceiling for — drawn against
        that ceiling rather than reported as a bare number.

        A single number said what the app is using and left "of how much?" to
        a note underneath, which is the half of the question a customer opens
        this screen with. The chart answers both at once: the plot is the use,
        the top of the Y axis is the limit, and the distance between them is
        the headroom. It is the reading the Overview drew before the restyle,
        in the shape the Metrics screen now draws it.

        Three columns, the first one three-quarters of the others, and the grid
        below has the same template with the log well spanning the last two —
        so the seam between the columns falls at the same x in both rows and
        the board reads as two columns rather than as two rows that nearly line
        up. Change the template or the gap here and change them there.

        The narrow column was a full third. On a laptop that made the
        configuration card as wide as a chart and left the log well wrapping
        every other line; the card's values are short and truncate cleanly,
        the log's are neither.

        One column on a phone rather than two: a plot 170px wide is a smear,
        and these are the two cards on the screen that are pictures. */}
    <div className="grid grid-cols-1 gap-3 lg:gap-4 laptop:grid-cols-[minmax(0,0.75fr)_minmax(0,1fr)_minmax(0,1fr)] laptop:gap-5">
      {/* The plan sits with the readings it is the ceiling for: the CPU and
          RAM charts beside it are plotted against these numbers, and they used
          to be a column apart. It is written as a stat too — eyebrow, value,
          unit, note — because a card of nothing but 12px label/value pairs
          read as a footnote between two headlines. The tier is the value here;
          the allowances are the note, and their units name them.

          It reads top-aligned like every other card here. Spreading its three
          lines down the taller row the charts set was worse than the gap it
          was meant to fill: a card whose eyebrow, value and note each sit a
          finger apart reads as three cards.

          First in the source and first on the board: the narrow column on
          the left suits three short lines, and the charts get the two wide
          columns over the log well. (Trial: the narrow column used to be the
          right-hand one.) */}
      {planCard}
      {/* The series is already a percentage of the container's own limit, so
          the ceiling is 100 and the label names what the 100 is worth. */}
      <MetricChart
        label={<>cpu {plan?.maxCPU ? <span className="text-navy-400">· limit {plan.maxCPU} vCPU</span> : null}</>}
        data={cpuGraphProps?.data}
        color="#4FA7EF"
        colors={cpuGraphProps?.colors}
        yMax={100}
        format={(value) => `${value.toFixed(0)}%`}
        height={OVERVIEW_PLOT_HEIGHT}
        timeWindow={containerStatsTimeWindow}
        emptyAsZero
        isLoading={isLoadingStats && !!mainServiceEnvironmentId}
        errorMessage={loadingStatsErrorMessage}
      />
      {/* The plan's own allowance is the ceiling, not the peak of the window:
          a chart that rescales to whatever the app happened to use draws an
          idle container at the top of the card. The series builder's `max`
          only stands in where there is no plan to read — and it floors at
          256MB, which would put the axis and the label at odds. */}
      <MetricChart
        label={<>ram {planRAM ? <span className="text-navy-400">· limit {planRAM}</span> : null}</>}
        data={ramGraphProps?.data}
        color="#4AC77B"
        colors={ramGraphProps?.colors}
        yMax={ramLimitBytes ?? ramGraphProps?.max}
        format={(bytes) => `${Math.round(bytes / (1024 * 1024))}`}
        unit="MB"
        height={OVERVIEW_PLOT_HEIGHT}
        timeWindow={containerStatsTimeWindow}
        emptyAsZero
        isLoading={isLoadingStats && !!mainServiceEnvironmentId}
        errorMessage={loadingStatsErrorMessage}
      />
    </div>

    {/* The security block is APP state, not deployment state, and it is shown
        ahead of everything else and outside the lastDeployment.error chain
        below. Two reasons: an app blocked by support from the review queue
        has an old successful deployment as its last one, so the deployment
        chain would render nothing at all; and a customer seeing both a
        "blocked" card and an unrelated build error at once cannot tell which
        one matters. */}
    {app.block ? (
      <SecurityBlockErrorMessage
        appId={app.id}
        block={app.block}
        userEmail={user.username}
      />
    ) : app.pendingFirstBuild && app.mainService?.mainServiceEnvironment ? (
      /* A notice with a button, not the form itself. This page is a board of
         panels; a long form dropped into the middle of it pushes everything
         real below the fold, and this is a decision made once rather than a
         permanent part of the page. Same shape as every other state reported
         here — payment pending, unverified email, security block. */
      <PendingSetupMessage
        appId={app.id}
        serviceEnvironment={app.mainService.mainServiceEnvironment}
        pendingFirstBuild={app.pendingFirstBuild}
        onReleased={async () => {
          await back4app2.releaseFirstBuild(
            app.pendingFirstBuild!.serviceEnvironmentId
          );
        }}
      />
    ) : app.status === AppStatus.PENDING_VERIFICATION ? <VerifyEmailErrorMessage userEmail={user.username} /> : (
      <>
        {app.mainService?.mainServiceEnvironment?.isPendingPayment || (!app.mainService?.mainServiceEnvironment?.isFreePlanElegible && app.mainService?.mainServiceEnvironment?.plan.name.includes('Free')) ? (
          <PaymentPendingErrorMessage appId={app.id} mainServiceEnvironment={app.mainService.mainServiceEnvironment} user={user} onPaid={() => window.location.reload()} />
        ) : (
          <>
            {lastDeployment?.error ? 
              lastDeployment?.error instanceof DockerfileNotFoundError ? <DockerfileErrorMessage /> 
              : lastDeployment?.error instanceof KanikoImageBuildError ? <ImageBuildError errorMessage={lastDeployment.error.message} /> 
              : lastDeployment?.error instanceof DeploymentMemoryExceededError ? <OutOfMemoryError appId={app.id} /> 
              : lastDeployment?.error instanceof GitHubResourceNotFoundError ? <ResourceNotAccessibleError />
              : lastDeployment?.error instanceof CreditCardValidationRequiredError ? <CreditCardValidationRequiredErrorMessage />
              : lastDeployment?.error instanceof GitRepositoryEmptyError ? null
              : null : null}
            {/* No sleeping-container card. A sleeping deployment wakes on the
                first request that reaches it — the balancer does it inline,
                see `enabledRule.deploymentIsAwake` in the http-balancer — so
                the state is transient and self-healing, and a card explaining
                it sat on the Overview looking like a fault to be fixed. The
                status card reads Available either way, which is what the
                address does. */}
            {/* Same columns and same gap as the stat row above, so the seam
                between the two columns lands under the one up there.

                The columns stretch rather than starting where they start: the
                log well takes whatever height the cards beside it come to, so
                the two columns end on one line. The `basis` below is the floor
                — an app with no service environment has only the plan card on
                the left, and the well should not shrink to fit that. */}
            <div className="grid gap-5 grid-cols-[minmax(0,1fr)] laptop:grid-cols-[minmax(0,0.75fr)_minmax(0,1fr)_minmax(0,1fr)]">

              {/* The phone's own log card. It was a door for a while — one
                  row that opened the Logs screen — and the two things wrong
                  with that were that the deployment log is not on that screen
                  at all, and that reading a deploy is the reason you opened
                  the app on a phone in the first place.

                  So: the same two tabs, the same wells, and nothing else. No
                  deployment meta line — the tab says which log you are
                  reading, and a deploy's own facts (id, commit, branch) are
                  what the Deployments screen is. The well is shorter than the
                  desktop one and the expand button inside it goes full
                  screen. The card below, from `laptop` up, is untouched. */}
              <SurfaceCard tone="surface" className="laptop:hidden flex flex-col overflow-hidden">
                <div className="flex-none px-3 py-2.5 border-b border-navy-700 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  {/* `touch`, because here the tabs are a thumb's target and
                      not a pointer's. */}
                  <SegmentedControl
                    ariaLabel="Log source"
                    size="touch"
                    value={logTab}
                    onChange={setLogTab}
                    segments={[
                      { value: 'build', label: 'Deployment log' },
                      { value: 'runtime', label: 'Runtime log' },
                    ]}
                  />
                  {/* The words the bottom bar uses, because that is where
                      these two go. */}
                  <button
                    type="button"
                    onClick={() => navigate(`/apps/${app.id}/${logTab === 'build' ? 'deployments' : 'logs'}`)}
                    className="b4-focus flex-none min-h-[32px] px-1 rounded-control text-ds-125 font-semibold text-action-soft"
                  >
                    {logTab === 'build' ? 'All deploys' : 'All logs'}
                  </button>
                </div>
                {/* 17rem of log: enough to read the tail of a build without
                    the card owning the screen the way the 26rem desktop well
                    would on a phone. */}
                <div className="grow shrink basis-[17rem] min-h-0">
                  {logTab === 'build' ? (
                    lastDeployment ? (
                      <LogBox
                        errorMessage={deploymentLogsLoadingErrorMessage}
                        logs={deploymentLogs}
                        isLoading={isLoadingDeploymentLogs}
                        canMaximize
                        fullHeight
                        hasFinished={hasFinishedBuilding(lastDeployment)}
                      />
                    ) : (
                      <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
                        Nothing built yet.
                      </div>
                    )
                  ) : (
                    mainServiceEnvironmentId ? (
                      <ContainerLogBox
                        errorMessage={containerLogsLoadingErrorMessage}
                        logs={containerLogs}
                        isLoading={isLoadingContainerLogs}
                        canMaximize
                        fullHeight
                      />
                    ) : (
                      <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
                        No container running.
                      </div>
                    )
                  )}
                </div>
              </SurfaceCard>

              <SurfaceCard tone="surface" className="hidden laptop:flex laptop:col-span-2 laptop:order-last flex-col overflow-hidden">
                {/* Open logs belongs with the tabs: all three choose which
                    view of the log you are in. Down on the deployment line it
                    sat among facts about the deploy, next to Cancel, which is
                    an action on the deploy and not on the view. */}
                <div className="flex-none px-4 py-3 border-b border-navy-700 flex items-center justify-between gap-3">
                  <SegmentedControl
                    ariaLabel="Log source"
                    value={logTab}
                    onChange={setLogTab}
                    segments={[
                      { value: 'build', label: 'Deployment log' },
                      { value: 'runtime', label: 'Runtime log' },
                    ]}
                  />
                  {/* Each tab's "more of this" goes where more of it is. The
                      Logs screen is runtime only — a build log belongs to the
                      deploy that made it, and that is Deployments — so on the
                      build tab this link used to land somewhere that could not
                      show what you were reading. */}
                  <button
                    type="button"
                    onClick={() => navigate(`/apps/${app.id}/${logTab === 'build' ? 'deployments' : 'logs'}`)}
                    className="b4-focus flex-none rounded-control text-ds-125 font-semibold text-action-soft hover:text-white"
                  >
                    {logTab === 'build' ? 'Open deployments' : 'Open logs'}
                  </button>
                </div>
                {/* What is being read, and what can be done to it. This line
                    carried the deployment's id alone; the card above it
                    carried the id and the commit message, which is one screen
                    saying the same thing in two places. The card went, and
                    what it said comes here.

                    Two lines, because four facts on one were a sentence: what
                    was deployed on the left — the id nobody types, the branch,
                    the message a person wrote — and how it went on the right,
                    where the state, the time it took and the age of the commit
                    line up right-edged. The deploy's own clock is not here; it
                    is beside the heading, where the app's state is.

                    A fixed height, sized for those two lines. The runtime tab
                    has one line to put here, and with the line taking its
                    height from its content the card grew and shrank with the
                    tab — and the configuration rows beside it, which share
                    the card's height, moved every time the tab changed. */}
                <div className="flex-none h-[68px] px-4 bg-navy-900 border-b border-navy-700 flex items-center justify-between gap-4">
                  {logTab === 'build' ? (
                    lastDeployment ? (<>
                      <div className="flex-1 min-w-0 flex flex-col gap-1">
                        <div className="flex items-center gap-2.5 min-w-0 font-courrier text-ds-115 text-navy-300">
                          {/* The whole uuid, not the first eight of it. Our
                              deployment ids are uuids and a person may need to
                              paste one into support; truncated to eight hex
                              characters it reads as a commit sha, which is a
                              different thing this screen also talks about. */}
                          <span className="flex-none">{lastDeployment.id}</span>
                          {/* A deployment from an image has no branch. It shows
                              the digest instead — a tag can point somewhere else
                              by the next deploy and the digest cannot. */}
                          {lastDeployment.imageDigest ? (
                            <span className="truncate" title={lastDeployment.imageDigest}>
                              {lastDeployment.imageDigest.slice(0, 19)}…
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 min-w-0">
                              {/* No branch and no digest: built from an upload. */}
                              {lastDeployment.branchName ? <BranchIcon size={12} /> : <UploadIcon size={12} />}
                              <span className="truncate">{lastDeployment.branchName || 'Upload'}</span>
                            </span>
                          )}
                        </div>
                        <span className="text-ds-125 text-navy-100 truncate" title={lastDeployment.commitMessage || undefined}>
                          {lastDeployment.commitMessage || 'no commit message'}
                        </span>
                      </div>

                      <div className="flex-none flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2.5">
                          <MyAppDeploymentStatus deployment={lastDeployment} />
                          <Timer
                            realElapsedMS={(lastDeployment.deployTask && (lastDeployment.deployTask.elapsedTimeMS + (lastDeployment.deployTask.finishedAt ? 0 : new Date().getTime() - appLoadedAt.getTime()))) || 0}
                            hasFinished={(lastDeployment.deployTask && lastDeployment.deployTask.finishedAt instanceof Date) || false}
                          />
                        </div>
                        {lastDeployment.committedAt ? (
                          <span className="font-courrier text-ds-115 text-navy-300 whitespace-nowrap">
                            {timeFromNow(lastDeployment.committedAt)}
                          </span>
                        ) : null}
                      </div>
                    </>) : (
                      <span className="font-courrier text-ds-115 text-navy-300">no deployment yet</span>
                    )
                  ) : (
                    <span className="font-courrier text-ds-115 text-navy-300">container output</span>
                  )}
                  {/* The one action that belongs beside the deploy rather
                      than beside the log, and only while there is a deploy to
                      stop. */}
                  {isBuildCancelable(lastDeployment) ? (
                    <CancelBuildButton deployment={lastDeployment!} />
                  ) : null}
                </div>
                {/* `basis` rather than a min-height: the row is auto-sized, so
                    an intrinsic height here would be the height of the whole
                    log. The well starts at 26rem and grows into whatever the
                    cards beside it come to. */}
                <div className="grow shrink basis-[26rem] min-h-0">
                  {logTab === 'build' ? (
                    lastDeployment ? (
                      <LogBox
                        errorMessage={deploymentLogsLoadingErrorMessage}
                        logs={deploymentLogs}
                        isLoading={isLoadingDeploymentLogs}
                        canMaximize
                        fullHeight
                        hasFinished={hasFinishedBuilding(lastDeployment)}
                      />
                    ) : (
                      <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
                        Nothing built yet.
                      </div>
                    )
                  ) : (
                    mainServiceEnvironmentId ? (
                      <ContainerLogBox
                        errorMessage={containerLogsLoadingErrorMessage}
                        logs={containerLogs}
                        isLoading={isLoadingContainerLogs}
                        canMaximize
                        fullHeight
                      />
                    ) : (
                      <div className="h-full bg-navy-950 flex items-center justify-center text-ds-135 text-navy-300">
                        No container running.
                      </div>
                    )
                  )}
                </div>
              </SurfaceCard>

              <div className="flex flex-col gap-5">
                {/* What this app is set to, and the way to each screen that
                    sets it. It began as the publish drawer's "what will
                    happen" — source, port, visibility — which answered for the
                    deploy and stopped there; the questions people came to the
                    Overview with ("how many variables does it have?", "is the
                    health check on?") were a click into Settings away with no
                    hint of the answer.

                    Every row is a button into its own section, so the summary
                    is also the shortest route to changing what it reports. And
                    the rows share the height rather than sitting at the top of
                    it, so the column ends where the log well does. */}
                {mainServiceEnvironment ? (
                  <SurfaceCard className="p-4 flex flex-col gap-3.5 laptop:flex-1">
                    <Eyebrow>configuration</Eyebrow>
                    <div className="-mx-4 flex flex-col laptop:flex-1 [&>*]:laptop:flex-1">
                      {/* The address, at the top of the card, as the one row
                          here that leaves the dashboard instead of opening a
                          settings screen — so its trailing mark is the
                          external-link glyph and not the chevron the rest
                          carry.

                          The domains row below counts domains; this one is
                          the way in. Same rule as the sidebar's link for when
                          there is no address to show: an expired domain has
                          nothing listening, and a blocked app must not
                          advertise one at all. */}
                      {appUrl ? (
                        <TileRow
                          dense
                          isFirst
                          tile={<LinkIcon size={22} />}
                          eyebrow="url"
                          value={appUrl}
                          title={appUrl}
                          href={`${window.location.protocol}//${appUrl}`}
                          trailing={<ExternalLinkIcon size={14} className="flex-none text-navy-300" />}
                        />
                      ) : null}
                      <TileRow
                        dense
                        isFirst={!appUrl}
                        tile={<FileIcon size={22} />}
                        eyebrow="source"
                        value={{
                          GITHUB: `GitHub · ${mainServiceEnvironment.branchName}${mainServiceEnvironment.autoDeploy ? ' · auto' : ''}`,
                          DOCKER_HUB: `Image · ${mainServiceEnvironment.imageRepository || 'image'}:${mainServiceEnvironment.imageTag || 'latest'}`,
                          // No repository and no push: each new upload is a deploy.
                          UPLOAD: 'Upload · files from your computer',
                        }[appSourceKind(mainServiceEnvironment, !!app.mainService?.repository)]}
                        onClick={() => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'source')}
                        trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                      />
                      {/* Build method and root directory used to share one
                          row, joined by a dot — two unrelated answers reading
                          as one value, and the folder path (the longer of the
                          two) was the half that got truncated away. They are
                          separate questions and get a row each.

                          Neither is asked of an app running a published image:
                          it is not built here, so there is no Dockerfile to
                          choose over buildpacks and no folder to build from. */}
                      {isBuiltFromSource(appSourceKind(mainServiceEnvironment, !!app.mainService?.repository)) ? (<>
                        <TileRow
                          dense
                          tile={<BoxIcon size={22} />}
                          eyebrow="build"
                          value={mainServiceEnvironment.isStaticSite
                            ? 'Static site'
                            : mainServiceEnvironment.builderPreference === 'BUILDPACK' ? 'Buildpack' : 'Dockerfile'}
                          onClick={() => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'build')}
                          trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                        />
                        <TileRow
                          dense
                          tile={<FolderIcon size={22} />}
                          eyebrow="root directory"
                          // The root is written four ways depending on when the
                          // app was made — '', '/', '.' and './' all mean it.
                          value={['', '/', './', '.'].includes(mainServiceEnvironment.rootDirPath || '')
                            ? 'repository root'
                            : mainServiceEnvironment.rootDirPath}
                          onClick={() => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'root-directory')}
                          trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                        />
                      </>) : null}
                      {/* The port and the command used to share a row behind a
                          dot. A start command is the longest string on this
                          card — `gunicorn app:app --bind 0.0.0.0:8000` — so
                          the half that got truncated was always the half with
                          something to say, and the port it was truncating for
                          is four characters. */}
                      <TileRow
                        dense
                        tile={<PortIcon size={22} />}
                        eyebrow="port"
                        value={mainServiceEnvironment.exposedPort
                          ? `${mainServiceEnvironment.exposedPort}/tcp`
                          : 'auto'}
                        onClick={() => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'port')}
                        trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                      />
                      <TileRow
                        dense
                        tile={<TerminalIcon size={22} />}
                        eyebrow="start command"
                        // Empty is not "unset": it is the documented default,
                        // and the settings field says so in its own words.
                        value={mainServiceEnvironment.startCommand || 'Procfile or start script'}
                        onClick={() => goToSettingsTab(MyAppSettingsTab.BUILD_AND_DEPLOY, 'start-command')}
                        trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                      />
                      <TileRow
                        dense
                        tile={<VariablesIcon size={22} />}
                        eyebrow="env variables"
                        value={envVarCount === 0
                          ? 'none set'
                          : `${envVarCount} ${envVarCount === 1 ? 'variable' : 'variables'}`}
                        onClick={() => goToSettingsTab(MyAppSettingsTab.VARIABLES)}
                        trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                      />
                      <TileRow
                        dense
                        tile={<GlobeIcon size={22} />}
                        eyebrow="domains"
                        // How many, not which one. A domain name is the one
                        // value on this card with no useful truncation — the
                        // tail is where it differs — and the row was carrying
                        // the main one plus a `+2` for the rest, which is a
                        // count written twice. Every row here is a door; this
                        // one now says how much is behind it, like the
                        // variables row above it.
                        value={app.block
                          ? 'hidden while under review'
                          : domainCount === undefined
                            ? (mainServiceEnvironment.mainCustomDomain?.name || 'no URL yet')
                            : domainCount === 0
                              ? 'no domains yet'
                              : `${domainCount} ${domainCount === 1 ? 'domain' : 'domains'}`}
                        onClick={() => goToSettingsTab(MyAppSettingsTab.CUSTOM_DOMAIN)}
                        trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                      />
                      <TileRow
                        dense
                        tile={<HeartbeatIcon size={22} />}
                        eyebrow="health check"
                        value={mainServiceEnvironment.healthCheckEndpoint || 'not set'}
                        onClick={() => goToSettingsTab(MyAppSettingsTab.HEALTH_AND_ALERT, 'health-check')}
                        trailing={<ChevronRightIcon size={14} className="flex-none text-navy-300" />}
                      />
                    </div>
                  </SurfaceCard>
                ) : null}
              </div>
            </div>
          </>
        )}
      </>
    )}
    <div className="mt-14">
      <Guides />
    </div>
  </>);
};

export default MyAppOverview;
